import { useQuery } from '@apollo/client';
import {
  CardPage,
  COMMON_CONSTANTS,
  SkeletonTableLoading,
  AppContext,
  useUserMSPCheckHook
} from '@nile/components';
import {
  GET_ENTITY_PROPERTIES,
  GET_TICKET_LISTVIEW_META,
  RMMClient,
  GET_PERSONALIZATION_BY_NAME
} from '@nile/graphql';
import ChartContext from '@nile/reports/src/modules/charts/contexts/Chart';
import AssetAlerts from '@nile/rmm/src/modules/asset-management/components/sub-pages/asset-alerts/asset-alerts';
import PatchAssetList from '@nile/rmm/src/modules/asset-management/components/sub-pages/asset-patch-report/index';
import AssetList from '@nile/rmm/src/modules/asset-management/components/sub-pages/asset/index';
import InvoiceList from '@nile/client_management/src/Pages/Protected/ClientNavigation/InvoiceContainer';
import InvoiceAuditList from '@nile/client_management/src/Pages/Protected/ClientNavigation/InvoiceAuditDataTable';
import { defaultPersonalization } from '@nile/mangifera/src/Constants/Tickets/index';
import ReportsContext from '@nile/reports/src/modules/home/contexts/Reports';
import { preFormatFilterObject } from '@nile/reports/src/modules/component/helper';
import {
  Collapsible,
  Heading,
  Button,
  Popover,
  ActionList,
  Page,
  Modal,
  Tippy
} from '@superops/ui-design-system';
import { AccountClient, GET_VIEWS } from '@nile/graphql';
import { getTranslatedText, useActivityTracker, toast } from '@nile/components';
import { DATA_REPORT_TABLE } from '@nile/reports/src/modules/charts/constants';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { cloneDeep, isEmpty } from 'lodash';
import AllTicketsTable from '@nile/client_management/src/Containers/ClientDetails/AllTicketsTable/AllTicketsTableNew';
import AssetSoftwareList from './Table/AssetSoftwareList';
import WorklogEntryDataTable from './Table/WorklogEntryDataTable';
import AllAssetsManagementView from '@nile/rmm/src/modules/asset-management/components/sub-pages/all-asset-management-view/all-assets-management-view';
import RMMContext from '@nile/rmm/src/modules/common/contexts/RMMContext';
import { EXPORT_REPORT } from '../../../../DataReports/graphql/query';
import './styles.scss';

const VIEW_MAP = {
  Invoice: 'INVOICE',
  NmAsset: 'NETWORK_MONITORING'
};

const DataTable = (props) => {
  const {
    isTableOpen = false,
    isTableEditable = true,
    disableToggle = false,
    noBulkSelect = true,
    showExportButton = false,
    intl,
    tableHeader = false,
    isFromWidgetView = false,
    defaultCondition = null
  } = props;
  const context = useContext(ChartContext);
  const reportsContext = useContext(ReportsContext);
  const appContext = useContext(AppContext);
  const { user } = appContext;
  const isMSPProduct = useUserMSPCheckHook();
  const [active, setActive] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [entitiesMeta, setEntitiesMeta] = useState([]);
  const [assetTableOptions, setAssetTableOptions] = useState({}); //used only for asset table
  const [isViewChange, setIsViewChange] = useState(true); //used only for asset table
  const [conditionState, setConditionState] = useState(null); //used only for asset table which prevents to many renders
  const [views, setViews] = useState([]);
  const [openMetrics, setOpenMetrics] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const toggleMetrics = useCallback(
    () => setOpenMetrics((prevState) => !prevState),
    []
  );

  /**
   * Boolean to check if the table is active i.e. non collapsed
   */
  const isTableActive = useCallback(() => {
    return active || isTableOpen;
  }, [active, isTableOpen]);

  const [bulkRecords, setBulkRecords] = useState([]);
  const [columnConfigurations, setColumnConfigurations] = useState();
  const [restrictFilter, setRestrictFilter] = useState(true);
  const [ticketCondition, setTicketCondition] = useState(null);

  const {
    data: columnPersonalisationData,
    error: personalizationError
  } = useQuery(GET_PERSONALIZATION_BY_NAME, {
    fetchPolicy: 'cache-and-network',
    skip: context.value.entity !== 'Ticket',
    client: AccountClient,
    variables: { userId: user?.userId, category: 'TicketView', name: '1' }
  });

  useEffect(() => {
    if (personalizationError) {
      console.error(
        'Failed to fetch personalization data:',
        personalizationError
      );
    }
  }, [personalizationError]);

  useEffect(() => {
    if (context?.value?.metrics?.length) {
      setAvailableMetrics(
        context.value.metrics.map((met) => ({
          ...met,
          label: met.name,
          value: met.metricId
        }))
      );
    }
  }, [context.value.metrics]);

  const getFilterCondition = () => {
    if (selectedMetric?.filterCondition?.operands?.length) {
      return selectedMetric.filterCondition;
    }

    return null;
  };

  const getCondition = () => {
    const { operands, reportCondition } = preFormatFilterObject({
      entity: context?.value?.entity,
      filterObj: cloneDeep(
        reportsContext.reportFilter?.current &&
          reportsContext.reportFilter?.current?.[reportsContext.activeView]
      ),
      removeLabel: true
    });
    let metricFilter = getFilterCondition();
    if (reportCondition) {
      metricFilter = metricFilter
        ? {
            ...metricFilter,
            operands: metricFilter.operands.concat({
              joinOperator: 'AND',
              operands: operands
            })
          }
        : {
            joinOperator: 'AND',
            operands: operands
          };
    }

    if (context.value.entity === 'HoursWorklogEntry') {
      const worklogCond = {
        joinOperator: 'AND',
        operands: [
          {
            attribute: 'serviceItem.quantityType',
            operator: 'is',
            value: 'HOURS'
          }
        ]
      };
      metricFilter = metricFilter
        ? {
            ...metricFilter,
            operands: (metricFilter?.operands || [])?.concat(worklogCond)
          }
        : worklogCond;
    }

    if (context.value.entity === 'NMAsset') {
      const nmaAssetCondition = {
        joinOperator: 'AND',
        operands: [
          {
            attribute: 'assetClass.assetClassModule.module',
            operator: 'is',
            value: 'NETWORK_MONITORING'
          }
        ]
      };
      metricFilter = metricFilter
        ? {
            ...metricFilter,
            operands: (metricFilter?.operands || [])?.concat(nmaAssetCondition)
          }
        : nmaAssetCondition;
    }
    if (defaultCondition) {
      return defaultCondition;
    }
    return metricFilter;
  };

  const conditions = getCondition();

  const handleAssetTableFilterChange = (filters) => {
    //this fn is used only for asset table
    setAssetTableOptions({ [context.value.entity]: { ...filters } });
    setIsViewChange(false);
  };

  const getViews = () => {
    AccountClient.query({
      query: GET_VIEWS,
      variables: {
        modules: Object.values(VIEW_MAP)
      }
    }).then(({ data }) => {
      if (data?.getViews) {
        setViews(data?.getViews);
      }
    });
  };

  const { loading: entityPropertiesLoading, data: entityProperties } = useQuery(
    GET_ENTITY_PROPERTIES
  );

  const {
    loading: ticketListViewMetaLoading,
    data: ticketListViewMeta
  } = useQuery(GET_TICKET_LISTVIEW_META, {
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
  });

  const providerValue = {
    setBulkRecords,
    setColumnConfigurations,
    restrictFilter
  };

  useEffect(() => {
    setConditionState(conditions);
  }, [selectedMetric]);

  useEffect(() => {
    getViews();
  }, []);

  const renderTable = () => {
    const tableViewId = views?.find(
      (view) =>
        view.module === VIEW_MAP?.[context.value.entity] ||
        view.module === context.value.entity
    );
    const defaultView =
      tableViewId?.views?.find(
        (view) => !view.customView && view?.name?.toLowerCase()?.includes('all')
      )?.id || tableViewId?.views?.[0]?.id;

    switch (context.value.entity) {
      case 'WorkStation':
        return (
          <AssetList
            entitiesMeta={entitiesMeta}
            defaultCondition={conditions}
          />
        );
      case 'AllAssets':
        return (
          <RMMContext.Provider value={providerValue}>
            <AllAssetsManagementView
              tab={'7501'}
              defaultListFilters={conditions}
              activeView={{ viewSubType: 'ALL_ASSET' }}
              isViewChange={isViewChange}
              noBulkSelect
              reRenderOnDefaultFilterChange
              handleFilterChange={handleAssetTableFilterChange}
              listInfo={assetTableOptions[context.value.entity]}
            />
          </RMMContext.Provider>
        );
      case 'NMAsset':
        return (
          <RMMContext.Provider value={providerValue}>
            <AllAssetsManagementView
              tab={'4001'}
              defaultListFilters={conditionState}
              activeView={{ viewSubType: 'ALL_MONITORED_ASSET' }}
              isViewChange={isViewChange}
              noBulkSelect
              reRenderOnDefaultFilterChange
              handleFilterChange={handleAssetTableFilterChange}
              listInfo={assetTableOptions[context.value.entity]}
            />
          </RMMContext.Provider>
        );
      case 'Alert':
        return (
          <AssetAlerts
            tableType="reportTableTable"
            entitiesMeta={entitiesMeta}
            defaultCondition={conditions}
            isTableEditable={isTableEditable}
            noBulkSelect={noBulkSelect}
          />
        );
      case 'PatchHealth':
      case 'AntiVirusHealth':
      case 'DataBackupHealth':
        return (
          <>
            {selectedMetric?.metricId ? (
              <PatchAssetList
                entitiesMeta={entitiesMeta}
                defaultCondition={conditions}
                chartModule={context.value.entity}
                metricId={selectedMetric}
                handleFilterChange={handleAssetTableFilterChange}
                listInfo={assetTableOptions[context.value.entity]}
              />
            ) : (
              <></>
            )}
          </>
        );
      case 'Ticket':
        return (
          <div className="plr-20">
            <AllTicketsTable
              entitiesMeta={entityProperties.getEntitiesMeta}
              ticketViewMeta={ticketListViewMeta.getTicketListViewMetaInfo}
              allowEdit={false}
              defaultFilter={conditions}
              defaultPersonalisation={
                columnPersonalisationData?.getPersonalizationByCategoryAndName
                  ?.value
                  ? JSON.parse(
                      columnPersonalisationData
                        .getPersonalizationByCategoryAndName.value
                    )
                  : defaultPersonalization(isMSPProduct)
              }
              propContext={{
                records: [],
                viewConditions: ticketCondition,
                setViewConditions: setTicketCondition
              }}
              noBulkSelect
              padded
            />
          </div>
        );
      case 'Invoice':
        return (
          <InvoiceList
            hideActions
            defaultCondition={conditions}
            tableType={DATA_REPORT_TABLE}
            defaultViewId={defaultView}
          />
        );
      case 'OfferedItem':
        return (
          <InvoiceAuditList
            defaultCondition={conditions}
            tableType={DATA_REPORT_TABLE}
            isTableEditable={isTableEditable}
          />
        );

      case 'HoursWorklogEntry':
        return (
          <WorklogEntryDataTable
            entitiesMeta={entitiesMeta}
            selectedMetric={selectedMetric}
            module={context.value.entity}
            defaultCondition={conditions}
          />
        );

      case 'AssetSoftwareInventory':
        return (
          <>
            {selectedMetric?.metricId ? (
              <AssetSoftwareList
                entitiesMeta={entitiesMeta}
                selectedMetric={selectedMetric}
                module={context.value.entity}
                defaultCondition={conditions}
              />
            ) : (
              <></>
            )}
          </>
        );
      default:
        return <></>;
    }
  };

  useEffect(() => {
    if (availableMetrics?.length) {
      const index = context.metricIndex || 0;
      setSelectedMetric(availableMetrics[index]);
    }
  }, [availableMetrics]);

  useQuery(GET_ENTITY_PROPERTIES, {
    client: RMMClient,
    onCompleted({ getEntitiesMeta }) {
      setEntitiesMeta(getEntitiesMeta);
    }
  });

  const handleToggle = useCallback(() => setActive((val) => !val), []);

  /**
   * Method to Export CSV from the data table
   */
  const exportCSV = async () => {
    let variable = {
      chartId: context.value.chartId
    };
    if (
      reportsContext?.reportCondition &&
      reportsContext?.reportCondition[reportsContext?.activeView]
    ) {
      const { operands, reportCondition } = await preFormatFilterObject({
        entity: context?.value?.entity,
        filterObj: cloneDeep(
          reportsContext.reportFilter?.current &&
            reportsContext.reportFilter?.current?.[reportsContext.activeView]
        ),
        removeLabel: true
      });

      variable = {
        ...variable,
        condition:
          (reportCondition &&
            !isEmpty(operands) && {
              joinOperator: 'AND',
              operands
            }) ||
          defaultCondition ||
          null
      };
    }
    if (defaultCondition) {
      variable = {
        ...variable,
        condition: defaultCondition
      };
    }
    AccountClient.query({
      query: EXPORT_REPORT,
      variables: {
        ExportReportIdentifierInput: variable
      },
      fetchPolicy: 'network-only'
    })
      .then((res) => {
        if (res?.data?.exportReport) {
          useActivityTracker({ event: 'EXPORT_CHART' });
          toast('success', 'app.toast.charts.exportCsv.success');
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => setIsExportModalOpen(false));
  };

  return (
    <CardPage
      className={`full-width dataTableCard white-grey-page ${
        active || isFromWidgetView ? 'full-height' : ''
      }`}
      maxWidth="100%"
      tableContent
      fullHeight
    >
      <Page
        variant="secondary"
        divider={tableHeader}
        contentHeight="80px"
        title={
          <div className="ptr" onClick={handleToggle}>
            <Heading element="h2">
              {tableHeader ? (
                tableHeader
              ) : (
                <FormattedMessage id="reports.chart.datatable.title" />
              )}
            </Heading>
          </div>
        }
        primaryAction={
          <div className="flex jc-spbtwn actr mr-10">
            <div className="flex actr">
              {isTableActive() && (
                <div className="mr-10" superid="expandDatatable">
                  <Popover
                    active={openMetrics}
                    preferredAlignment="right"
                    activator={
                      <Button onClick={toggleMetrics} disclosure>
                        {selectedMetric?.name}
                      </Button>
                    }
                    onClose={toggleMetrics}
                  >
                    <ActionList
                      actionRole="menuitem"
                      items={availableMetrics.map((metric) => ({
                        ...metric,
                        content: metric.name,
                        onAction: () => {
                          setSelectedMetric(metric);
                          if (
                            [
                              'NMAsset',
                              'AllAssets',
                              'PatchHealth',
                              'AntiVirusHealth',
                              'DataBackupHealth'
                            ].find((enty) =>
                              context?.value?.entity?.includes(enty)
                            )
                          ) {
                            //empty the filter condition on metric change only for asset Tables
                            handleAssetTableFilterChange({});
                          }
                          toggleMetrics();
                        }
                      }))}
                    />
                  </Popover>
                </div>
              )}
              {!context?.isEdit && (
                <div className="fx mr-10">
                  <Button
                    onClick={() => {
                      if (!active) handleToggle();
                      context.setIsTableExpanded(!context?.isTableExpanded);
                    }}
                    icon={
                      <SVGIcon
                        name={context?.isTableExpanded ? 'Collapse' : 'Expand'}
                      />
                    }
                  />
                </div>
              )}
              {!disableToggle && (
                <div className="flex-center">
                  <Button
                    onClick={handleToggle}
                    icon={
                      isTableActive() ? (
                        <SVGIcon name="ArrowDown" />
                      ) : (
                        <SVGIcon name="ArrowRight" />
                      )
                    }
                  />
                </div>
              )}
              {showExportButton && (
                <Modal
                  activator={
                    <Tippy
                      interactive
                      title={getTranslatedText(intl, 'common.exportCsv')}
                      position="bottom-end"
                    >
                      <Button
                        onClick={() => setIsExportModalOpen(true)}
                        icon={<SVGIcon name="Download" />}
                      />
                    </Tippy>
                  }
                  dialogClassName="export-modal-dialog"
                  backdropClassName="export-modal-backdrop"
                  open={isExportModalOpen}
                  onClose={() => setIsExportModalOpen(false)}
                  variant="confirmation"
                  title={
                    <FormattedMessage id="reports.chart.exportCsv.modal.header" />
                  }
                  primaryAction={{
                    content: (
                      <div className="flex actr">
                        <SVGIcon name="CircleTickOutlineMinor" />
                        <div className="ml8">
                          <FormattedMessage id="invoice.details.list.taxable" />
                        </div>
                      </div>
                    ),
                    onAction: exportCSV
                  }}
                  secondaryActions={[
                    {
                      content: <FormattedMessage id="common.cancel" />,
                      onAction: () => setIsExportModalOpen(false)
                    }
                  ]}
                >
                  <FormattedMessage id="reports.chart.exportCsv.modal.description" />
                </Modal>
              )}
            </div>
          </div>
        }
      >
        <Collapsible
          open={isTableActive()}
          transition={{ timingFunction: 'ease' }}
        >
          <div className={isFromWidgetView ? 'mb-10' : 'mlr-30 mb-30'}>
            {ticketListViewMetaLoading || entityPropertiesLoading ? (
              <div className="flex actr jctr fw bg-white h100">
                <SkeletonTableLoading />
              </div>
            ) : (
              !!selectedMetric && renderTable()
            )}
          </div>
        </Collapsible>
      </Page>
    </CardPage>
  );
};

export default injectIntl(DataTable);
